import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  createRef,
  RefObject
} from 'react'
import { DEFAULT_SIDEBAR_CONFIG, DEFAULT_SIDEBAR_STATE, INITIAL_LAYOUT_CONFIG, INITIAL_LAYOUT_STATE } from './constants'
import {
  ILayoutConfig,
  ILayoutState,
  ISidebarContext,
  ISidebarConfig,
  ISidebarState,
  ILayoutContext,
  ISidebarHandle
} from './types.d'

// export const layoutConfigReducer = (state: ILayoutConfig, action: LayoutConfigAction) => {
//   switch (action.type) {
//     case SET_LAYOUT_CONFIG:
//       return {
//         ...action.payload
//       }
//     case SET_APP_LAYOUT_CONFIG:
//       return {
//         ...state,
//         ...action.payload
//       }
//     default:
//       return state
//   }
// }

// export const layoutStateReducer = (state: ILayoutState, action: LayoutStateAction) => {
//   switch (action.type) {
//     case SET_APP_LAYOUT_STATE:
//       return {
//         ...state,
//         ...action.payload
//       }
//     case SET_LAYOUT_STATE:
//       return {
//         ...action.payload
//       }
//     default:
//       return state
//   }
// }

export const defaultSidebarHandle: ISidebarHandle = {
  getSidebarConfig: () => DEFAULT_SIDEBAR_CONFIG,
  setSidebarConfig: () => null,

  getSidebarState: () => DEFAULT_SIDEBAR_STATE,
  setSidebarState: () => null,

  collapseSidebar: () => null,
  expandSidebar: () => null,
  expandOverlaySidebar: () => null,
  toggleSidebar: () => null
}

const SidebarContext = createContext<ISidebarContext>({
  sidebarConfig: DEFAULT_SIDEBAR_CONFIG,
  sidebarState: DEFAULT_SIDEBAR_STATE,
  ...defaultSidebarHandle
})

export const SidebarProvider = ({
  appKey = 'default',
  initConfig = DEFAULT_SIDEBAR_CONFIG,
  initState = DEFAULT_SIDEBAR_STATE,
  children
}: {
  appKey?: string
  initConfig?: ISidebarConfig
  initState?: ISidebarState
  children: ReactNode
}) => {
  const [config, setConfig] = useState({ ...DEFAULT_SIDEBAR_CONFIG, ...initConfig })
  const [state, setState] = useState({ ...DEFAULT_SIDEBAR_STATE, ...initState })

  const layoutConfigRef = useRef<ILayoutConfig>(INITIAL_LAYOUT_CONFIG)
  const layoutStateRef = useRef<ILayoutState>(INITIAL_LAYOUT_STATE)

  useEffect(() => {
    try {
      const localLayoutConfig = localStorage.getItem('layoutConfig')
      if (localLayoutConfig) {
        layoutConfigRef.current = JSON.parse(localLayoutConfig)
        if (layoutConfigRef.current?.[appKey]?.sidebar) {
          setConfig({ ...config, ...layoutConfigRef.current[appKey].sidebar })
        }
      }

      const localLayoutState = localStorage.getItem('layoutState')
      if (localLayoutState) {
        layoutStateRef.current = JSON.parse(localLayoutState)
        if (layoutStateRef.current?.[appKey]?.sidebar) {
          setState({ ...state, ...layoutStateRef.current[appKey].sidebar })
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, [appKey])

  const getSidebarConfig = useCallback(() => config, [config])
  const getSidebarState = useCallback(() => state, [state])

  const setSidebarConfig = useCallback((config: ISidebarConfig) => {
    setConfig(config)
    const appLayoutConfig = { ...(layoutConfigRef.current?.[appKey] || {}) }
    appLayoutConfig.sidebar = config
    layoutConfigRef.current[appKey] = appLayoutConfig
    localStorage.setItem('layoutConfig', JSON.stringify(layoutConfigRef.current))
  }, [])

  const setSidebarState = useCallback((state: ISidebarState) => {
    setState(state)
    const appLayoutState = { ...(layoutStateRef.current?.[appKey] || {}) }
    appLayoutState.sidebar = state
    layoutStateRef.current[appKey] = appLayoutState
    localStorage.setItem('layoutState', JSON.stringify(layoutStateRef.current))
  }, [])

  const collapseSidebar = useCallback(() => {
    if (config.collapsible) {
      const newState = { ...state, collapsed: true, overlayExpanding: false }
      setSidebarState(newState)
    }
  }, [state, config])

  const expandSidebar = useCallback(() => {
    const newState = { ...state, collapsed: false, overlayExpanding: false }
    setSidebarState(newState)
  }, [state, config])

  const expandOverlaySidebar = useCallback(() => {
    const newState = { ...state, collapsed: true, overlayExpanding: true }
    setSidebarState(newState)
  }, [state, config])

  const toggleSidebar = useCallback(() => {
    const newState = { ...state, collapsed: !state.collapsed }
    setSidebarState(newState)
  }, [state, config])

  return (
    <SidebarContext.Provider
      value={{
        sidebarConfig: config,
        sidebarState: state,
        getSidebarConfig,
        setSidebarConfig,

        getSidebarState,
        setSidebarState,

        collapseSidebar,
        expandSidebar,
        expandOverlaySidebar,
        toggleSidebar
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export const useSidebarContext = () => {
  return useContext(SidebarContext)
}

const LayoutContext = createContext<ILayoutContext>({
  sidebarHandleRef: createRef<ISidebarHandle>()
})

export const LayoutProvider = ({
  sidebarHandleRef,
  children
}: {
  sidebarHandleRef: RefObject<ISidebarHandle>
  children: ReactNode
}) => {
  return <LayoutContext.Provider value={{ sidebarHandleRef: sidebarHandleRef }}>{children}</LayoutContext.Provider>
}

export const useLayoutContext = () => {
  return useContext(LayoutContext)
}
